import * as React from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Label, Navbar, NavbarBrand, Row } from 'reactstrap';
import { FaChevronDown, FaChevronUp, FaPrint } from 'react-icons/fa';
import { useContext, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { PeopleContext } from '../../contexts/PeopleContext';
import { IUser } from '../Users/UsersTable';
import { authAxios } from '../../services/AxiosService';
import { IHoursRecord } from '../Files/FileContext';
import { DomainContext, SUPPORT_REQUEST } from '../../contexts/DomainContext';
import { IndividualsContext } from '../../contexts/IndividualsContext';
import { formatDate, formatDateDayOfWeek, formatDateForServer } from '../../formatters/DateTimeFormatter';
import DatePicker from 'react-datepicker';
import { ReportContext } from './ReportContext';
import { Column, TableInstance, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import { useDefaultColumn } from 'src/hooks/ReactTableHooks';
import { IReportingSupportWorkerRequest } from 'src/components/SupportRequests/SupportWorkerRequestContext';

export const SupportWorkerWorkload = () => {
  const [selectedSupportWorker, setSelectedSupportWorker] = useState();
  const peopleContext = useContext(PeopleContext);
  const lawyers = peopleContext.lawyers;
  const individualsContext = useContext(IndividualsContext);
  const domainContext = useContext(DomainContext);
  const timeTrackingActivities = domainContext.timeTrackingActivities.filter((tta) => {
    return tta.userRole === 'SupportWorker';
  });
  const supportRequestCriteria = domainContext.supportRequestCriteria;
  const supportRequestAssistanceTypes = domainContext.supportRequestAssistanceTypes;
  const [filteredSupportWorkerRequests, setFilteredSupportWorkerRequests] = useState<IReportingSupportWorkerRequest[]>(
    []
  );
  const [supportWorkerRequestsCount, setSupportWorkerRequestsCount] = useState<number>(0);
  const individuals = individualsContext.state.individuals;
  const [userTimeTracking, setUserTimeTracking] = useState<IHoursRecord[]>([]);
  const [interventionTimeTracking, setInterventionTimeTracking] = useState<IHoursRecord[]>([]);
  const [combinedEntries, setCombinedEntries] = useState<IHoursRecord[]>([]);
  const [dailyTotals, setDailyTotals] = useState<Map<string, number>>(new Map());
  const reportContext = useContext(ReportContext);
  const reportGeneratedDate = reportContext.reportGeneratedDate;
  const allSupportWorkerRequests = reportContext.allSupportWorkerRequests;
  const fromDate = reportContext.fromDate;
  const setFromDate = reportContext.setFromDate;
  const toDate = reportContext.toDate;
  const setToDate = reportContext.setToDate;

  const selectedSupportWorkerUser = useMemo(
    () => peopleContext.supportWorkers.find((u: IUser) => u.userId === selectedSupportWorker),
    [peopleContext.supportWorkers, selectedSupportWorker]
  );

  useEffect(() => {
    if (selectedSupportWorkerUser && fromDate && toDate) {
      authAxios
        .get(
          `/api/users/admin/${selectedSupportWorkerUser.userId}/time-tracking?startDate=${formatDateForServer(
            fromDate
          )}&endDate=${formatDateForServer(toDate)}`
        )
        .then((response) => {
          setUserTimeTracking(response.data);
        });
      authAxios
        .get(
          `/api/support-worker-requests/time-tracking/${
            selectedSupportWorkerUser.userId
          }?startDate=${formatDateForServer(fromDate)}&endDate=${formatDateForServer(toDate)}`
        )
        .then((response) => {
          setInterventionTimeTracking(response.data);
        });
    } else {
      setInterventionTimeTracking([]);
      setUserTimeTracking([]);
    }
  }, [selectedSupportWorkerUser, toDate, fromDate]);

  useEffect(() => {
    setCombinedEntries([...userTimeTracking, ...interventionTimeTracking]);
  }, [userTimeTracking, interventionTimeTracking]);

  useEffect(() => {
    const mapTemp = new Map<string, number>();
    combinedEntries.forEach((e) => {
      const totalSoFar = mapTemp.get(formatDateForServer(new Date(e.date)));
      const updatedTotal: number = (totalSoFar ? Number(totalSoFar.toFixed(2)) : 0.0) + e.hours;
      mapTemp.set(formatDateForServer(new Date(e.date)), Number(updatedTotal.toFixed(2)));
    });
    setDailyTotals(mapTemp);
  }, [combinedEntries]);

  const totalDailyValuesFromMap = () => {
    let total = 0;
    dailyTotals.forEach((value) => {
      total = total + value;
    });
    return total.toFixed(2);
  };

  useEffect(() => {
    if (selectedSupportWorker) {
      setFilteredSupportWorkerRequests(
        allSupportWorkerRequests.filter(
          (r) =>
            r.submittedDate >= fromDate.getTime() &&
            r.submittedDate <= toDate.getTime() &&
            r.supportWorkerId === selectedSupportWorker
        )
      );
      setSupportWorkerRequestsCount(
        allSupportWorkerRequests.filter(
          (r) =>
            r.submittedDate >= fromDate.getTime() &&
            r.submittedDate <= toDate.getTime() &&
            r.supportWorkerId === selectedSupportWorker
        ).length
      );
    } else {
      setFilteredSupportWorkerRequests([]);
      setSupportWorkerRequestsCount(0);
    }
  }, [selectedSupportWorker, allSupportWorkerRequests, fromDate, toDate]);

  const supportWorkerRequestColumns: Column<IReportingSupportWorkerRequest>[] = useMemo(
    () => [
      {
        Header: 'Submitted Date',
        disableFilters: true,
        accessor: (r: IReportingSupportWorkerRequest) => {
          return formatDate(new Date(r.submittedDate));
        },
      },
      {
        Header: 'Client',
        id: 'clientId',
        disableFilters: true,
        accessor: (r: IReportingSupportWorkerRequest) => {
          return (
            individuals.find((i) => i.individualId === r.clientId)?.lastName.toUpperCase() +
            ', ' +
            individuals.find((i) => i.individualId === r.clientId)?.firstName
          );
        },
      },
      {
        Header: 'Lawyer',
        id: 'lawyerId',
        disableFilters: true,
        accessor: (r: IReportingSupportWorkerRequest) => {
          return (
            lawyers.find((l) => l.userId === r.lawyerId)?.lastName.toUpperCase() +
            ', ' +
            lawyers.find((i) => i.userId === r.lawyerId)?.firstName
          );
        },
      },
      {
        Header: 'Criteria For Assistance',
        id: 'criteriaForAssistance',
        disableFilters: true,
        accessor: (r: IReportingSupportWorkerRequest) => {
          const criteriaList = r.criteriaForAssistance.map((c) =>
            supportRequestCriteria.find((criteria) => c === criteria.supportRequestCriteriaId)
          );
          return criteriaList.map((criteria) => (criteria ? criteria?.name : '')).join(', ');
        },
      },
      {
        Header: 'Assistance Types',
        id: 'assistanceTypes',
        disableFilters: true,
        accessor: (r: IReportingSupportWorkerRequest) => {
          const assistanceList = r.assistanceTypes.map((a) =>
            supportRequestAssistanceTypes.find((assistanceType) => a === assistanceType.supportRequestAssistanceTypeId)
          );
          return assistanceList.map((assistance) => (assistance ? assistance?.name : '')).join(', ');
        },
      },
      {
        Header: 'Status',
        id: 'status',
        disableFilters: true,
        accessor: (r: IReportingSupportWorkerRequest) => {
          return r.status;
        },
        sortType: (rowA: any, rowB: any, id: any, desc: any) => {
          const statusRowA = SUPPORT_REQUEST.STATES.find((st) => st.value === rowA.original['status']);
          const statusRowB = SUPPORT_REQUEST.STATES.find((st) => st.value === rowB.original['status']);
          if (statusRowA && statusRowB) {
            if (statusRowA.order > statusRowB.order) return 1;
            if (statusRowB.order > statusRowA.order) return -1;
            return 0;
          } else if (statusRowA && !statusRowB) {
            return 1;
          } else if (statusRowB && !statusRowA) {
            return -1;
          } else {
            return 0;
          }
        },
      },
      {
        Header: 'Details',
        id: 'details',
        disableFilters: true,
        accessor: (r: IReportingSupportWorkerRequest) => {
          return r.details;
        },
      },
      {
        Header: 'Current Weight',
        id: 'currentWeight',
        disableFilters: true,
        accessor: (r: IReportingSupportWorkerRequest) => {
          return r.currentSupportWorkerRequestWeightString;
        },
      },
    ],
    [individuals, lawyers, supportRequestCriteria, supportRequestAssistanceTypes]
  );

  const timeTrackingColumns: Column<IHoursRecord>[] = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'date',
        Cell: (cell: any) => (cell?.row?.original?.date ? formatDateDayOfWeek(new Date(cell.row.original.date)) : ''),
        width: 70,
        minWidth: 70,
      },
      {
        Header: 'Activity',
        id: 'timeTrackingActivityId',
        accessor: (record: IHoursRecord) =>
          timeTrackingActivities.find((t) => t.timeTrackingActivityId === record.timeTrackingActivityId)?.name,
        width: 150,
        minWidth: 150,
      },
      {
        Header: 'Hours',
        id: 'hours',
        accessor: (cell: IHoursRecord) => {
          return Number(cell.hours.toFixed(2));
        },
        width: 50,
        minWidth: 50,
      },
      {
        Header: 'Hours Daily Total',
        accessor: 'hoursRecordId',
        width: 50,
        minWidth: 50,
        Cell: (cell: any) => {
          if (cell?.row?.original?.date) {
            const total = dailyTotals.get(formatDateForServer(new Date(cell.row.original.date)));
            return <span>{total ? total : ''}</span>;
          } else {
            return <span />;
          }
        },
      },
    ],
    // These dependencies should be reviewed and corrected, this was disabled only to clean up lint
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [combinedEntries, dailyTotals]
  );

  const supportWorkerRequestData: IReportingSupportWorkerRequest[] = useMemo(
    () => filteredSupportWorkerRequests,
    [filteredSupportWorkerRequests]
  );

  const timeTrackingData: IHoursRecord[] = useMemo(() => combinedEntries, [combinedEntries]);

  const defaultColumn = useDefaultColumn();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setPageSize,
  }: TableInstance<IReportingSupportWorkerRequest> = useTable(
    {
      columns: supportWorkerRequestColumns,
      data: supportWorkerRequestData,
      defaultColumn,
      initialState: {
        sortBy: [
          {
            id: 'status',
            desc: false,
          },
        ],
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps: getTimeTrackingTableProps,
    getTableBodyProps: getTimeTrackingTableBodyProps,
    headerGroups: timeTrackingHeaderGroups,

    prepareRow: prepareTimeTrackingRow,
    page: timeTrackingPage,
    setPageSize: setTimeTrackingPageSize,
  }: TableInstance<IHoursRecord> = useTable(
    {
      columns: timeTrackingColumns,
      data: timeTrackingData,
      defaultColumn,
      initialState: {
        sortBy: [
          {
            id: 'date',
            desc: true,
          },
        ],
      },
    },
    useSortBy,
    usePagination
  );

  //set page size to number of entries so they can all be printed at once
  useEffect(() => {
    if (supportWorkerRequestData.length > 0) {
      setPageSize(supportWorkerRequestData.length);
    } else {
      setPageSize(1);
    }
    if (timeTrackingData.length > 0) {
      setTimeTrackingPageSize(timeTrackingData.length);
    } else {
      setTimeTrackingPageSize(1);
    }
  }, [supportWorkerRequestData, timeTrackingData, setPageSize, setTimeTrackingPageSize]);

  return (
    <div>
      <Navbar color={'light'} light={true} expand={'xs'} className={'border-bottom sticky-top'}>
        <Row className={'flex-fill'}>
          <Col md={3} xl={2}>
            <NavbarBrand>Support Worker Requests By Support Worker</NavbarBrand>
          </Col>
          <Col className={'d-flex'}>
            <Button className={'ml-auto'} color={'warning'} onClick={() => window.print()}>
              Print <FaPrint />
            </Button>
          </Col>
        </Row>
      </Navbar>
      <Container className={'my-5 container--no-margin-print'}>
        <Row>
          <Col>
            <Card className={'no-print'}>
              <CardHeader>Select Date Range</CardHeader>
              <CardBody>
                <Row>
                  <Col>
                    <Label className={'flex second-label'} style={{ paddingRight: '10px' }}>
                      From
                    </Label>
                    <DatePicker
                      className={'form-control date-select flex'}
                      selected={fromDate}
                      onChange={(date) => {
                        if (date) {
                          setFromDate(date);
                        }
                      }}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      shouldCloseOnSelect={true}
                      dateFormat={'yyyy-MM-dd'}
                    />
                  </Col>
                  <Col>
                    <Label
                      className={'flex second-label'}
                      for={'EndDate'}
                      style={{ paddingRight: '10px', paddingLeft: '10px' }}
                    >
                      To
                    </Label>
                    <DatePicker
                      className={'form-control date-select flex'}
                      selected={toDate}
                      onChange={(date) => {
                        console.log(date?.getTime());
                        if (date) {
                          setToDate(date);
                        }
                      }}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      shouldCloseOnSelect={true}
                      dateFormat={'yyyy-MM-dd'}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Card className={'no-print'}>
              <CardHeader>Select Support Worker</CardHeader>
              <CardBody>
                <div className={'flex align-items-center'}>
                  <Label className={'flex second-label'} style={{ paddingRight: '13px' }}>
                    Support Worker
                  </Label>
                  <Select
                    style={{ maxWidth: '240px' }}
                    options={peopleContext.supportWorkers
                      .filter((e) => e.active)
                      .sort((a, b) => (a.lastName + a.firstName > b.lastName + b.firstName ? 1 : -1))}
                    placeholder={'Select support worker...'}
                    value={selectedSupportWorkerUser}
                    onChange={(value: any) => {
                      setSelectedSupportWorker(value?.userId || '');
                    }}
                    getOptionValue={(option: IUser) => option.userId}
                    getOptionLabel={(option: IUser) => option.lastName.toUpperCase() + ', ' + option.firstName}
                    isClearable={true}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <div className={'report mt-5'}>
          <header className={'report-header'}>
            <h2 id={'count-header'} className={'text-center'}>{`Support Worker Requests By Support Worker (${formatDate(
              reportGeneratedDate
            )})`}</h2>
            {selectedSupportWorker && (
              <h3 className={'text-center h5 text-muted'}>
                {'Number of Support Requests Submitted Between ' +
                  formatDate(fromDate) +
                  ' and ' +
                  formatDate(toDate) +
                  ' for ' +
                  selectedSupportWorkerUser?.lastName.toUpperCase() +
                  ', ' +
                  selectedSupportWorkerUser?.firstName +
                  ': ' +
                  supportWorkerRequestsCount}
              </h3>
            )}
          </header>
        </div>
        <div className={'report'} style={{ minHeight: '200px' }}>
          <table className={'table table-bordered report1'} {...getTableProps()}>
            <thead style={{ overflow: 'wrap', cursor: 'pointer' }}>
              {headerGroups.map((headerGroup: any, index: number) => (
                <tr key={`files-table-thead-tr-${index}`} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any) => (
                    <th
                      key={`files-table-thead-tr-${index}-${column.id}`}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      width={column.width}
                      className={'header'}
                      style={{ verticalAlign: 'middle' }}
                    >
                      {column.render('Header')}
                      <span>{column.isSorted && (column.isSortedDesc ? <FaChevronDown /> : <FaChevronUp />)}</span>
                      <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                        {column.canFilter ? column.render('Filter') : null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row: any) => {
                prepareRow(row);

                return (
                  <tr key={`files-table-tr-${row.id}`} {...row.getRowProps()}>
                    {row.cells.map((cell: any) => {
                      return (
                        <td
                          key={`files-table-td-${cell.row.id}-${cell.column.id}`}
                          {...cell.getCellProps([{ className: 'report-row' }])}
                          style={{ verticalAlign: 'middle' }}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className={'table-responsive'}>
          <div className={'report mt-5'}>
            <header className={'report-header'}>
              <h2 id={'count-header'} className={'text-center'}>{`Hours Recorded By Support Worker (${formatDate(
                reportGeneratedDate
              )})`}</h2>
              {selectedSupportWorker && (
                <h3 className={'text-center h5 text-muted'}>
                  {'Total Time Recorded Between ' +
                    formatDate(fromDate) +
                    ' and ' +
                    formatDate(toDate) +
                    ' for ' +
                    selectedSupportWorkerUser?.lastName.toUpperCase() +
                    ', ' +
                    selectedSupportWorkerUser?.firstName +
                    ': ' +
                    totalDailyValuesFromMap() +
                    ' hours'}
                </h3>
              )}
            </header>
          </div>
          <table
            className={'table table-bordered report1'}
            {...getTimeTrackingTableProps()}
            style={{ tableLayout: 'fixed' }}
          >
            <thead style={{ cursor: 'pointer' }}>
              {timeTrackingHeaderGroups.map((headerGroup: any, index: number) => (
                <tr key={`users-table-thead-tr-${index}`} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any) => (
                    <th
                      key={`users-table-thead-tr-${index}-${column.id}`}
                      {...column.getHeaderProps({
                        ...column.getSortByToggleProps(),
                        style: {
                          minWidth: column.minWidth,
                          width: column.width,
                          maxWidth: column.maxWidth,
                        },
                      })}
                    >
                      {column.render('Header')}
                      <span>{column.isSorted && (column.isSortedDesc ? <FaChevronDown /> : <FaChevronUp />)}</span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTimeTrackingTableBodyProps()}>
              {timeTrackingPage.map((row: any) => {
                prepareTimeTrackingRow(row);

                return (
                  <tr key={`users-table-tr-${row.id}`} {...row.getRowProps()}>
                    {row.cells.map((cell: any) => {
                      return (
                        <td
                          key={`users-table-td-${cell.row.id}-${cell.column.id}`}
                          {...cell.getCellProps()}
                          style={{ wordWrap: 'break-word' }}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Container>
    </div>
  );
};
